import ApiCodeBlock from "./DocumentationCodeBlock";

export default function APISection() {
  return (
    <div className="space-y-5 pb-4 text-neutral-950">
      <h1 className="text-lg leading-6 font-bold -tracking-wide">
        API Navigation Guide
      </h1>
      <div className="space-y-2">
        <h1
          id="auth-standards"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Authentication Standards
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          Always implement proper authentication using API key parameters:
        </p>
        <ApiCodeBlock
          method="GET"
          endpoint="/metawhale_v1/es/posts"
          xApiToken="your-api-key-here"
        />
      </div>
      <div className="space-y-2">
        <h1
          id="rest-nav"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          RESTful API Navigation
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          All requests require the{" "}
          <code className="font-bold">X-API-Token</code> request header for
          authentication. Query parameters for API keys are no longer supported.
        </p>

        <div className="space-y-1">
          <h1 className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
            GET Requests (Data Retrieval)
          </h1>
          <ApiCodeBlock
            method="GET"
            endpoint={[
              "/metawhale_v1/users",
              "/metawhale_v1/users/{id}",
              "/metawhale_v1/users?filter=active&limit=50",
            ]}
          />
        </div>

        <div className="space-y-1">
          <h1 className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
            POST Requests (Data Creation)
          </h1>
          <ApiCodeBlock
            method="POST"
            endpoint={["/metawhale_v1/users"]}
            contentType={{
              type: "application/json",
              body: {
                name: "John Doe",
                email: "john.doe@company.com",
                role: "analyst",
              },
            }}
          />
        </div>

        <div className="space-y-1">
          <h1 className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
            PUT/PATCH Requests (Data Updates)
          </h1>
          <ApiCodeBlock
            method="PATCH"
            endpoint={["/metawhale_v1/users/{id}"]}
            contentType={{
              type: "application/json",
              body: {
                status: "active",
              },
            }}
          />
        </div>
      </div>
      <div className="space-y-2">
        <h1
          id="error-handling"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Error Handling
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          Always implement comprehensive error handling:
        </p>
        <div className="space-y-1">
          <ApiCodeBlock
            language="javascript"
            code={`try {
  const response = await fetch('/api/v1/data?api_key=[your-api-key]');
  if (!response.ok) {
    throw new Error(\`HTTP error! status: \${response.status}\`);
  }
  const data = await response.json();
} catch (error) {
  console.error('API request failed:', error);
}`}
          />
        </div>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <h1
            id="rate-limit"
            className="text-[18px] leading-5 font-bold -tracking-wide"
          >
            Rate Limiting Compliance
          </h1>
          <p className="pl-2 text-sm leading-[140%] font-medium text-neutral-600">
            Our API enforces rate limits using a{" "}
            <strong>60-second sliding window</strong> algorithm. Limits are
            defined by your plan's <code>calls_per_min</code> field.
          </p>
          <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <li>
              Adhere to per-minute limits (check your plan for specific{" "}
              <code>calls_per_min</code>)
            </li>
            <li>
              Monitor <code>X-RateLimit</code> headers to track remaining quota
              in real-time
            </li>
            <li>
              Handle HTTP <code>429</code> responses by respecting the{" "}
              <code>retry_after</code> value
            </li>
            <li>Implement exponential backoff for non-429 transient errors</li>
          </ul>
        </div>

        {/* Optional: Header Reference Table */}
        <div className="pl-2">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="py-2 pr-4 font-bold">Header</th>
                <th className="py-2 font-bold">Description</th>
              </tr>
            </thead>
            <tbody className="text-neutral-700">
              <tr className="border-b border-neutral-100">
                <td className="py-2 pr-4 font-mono">X-RateLimit-Limit</td>
                <td className="py-2">Total requests allowed per minute</td>
              </tr>
              <tr className="border-b border-neutral-100">
                <td className="py-2 pr-4 font-mono">X-RateLimit-Remaining</td>
                <td className="py-2">Requests remaining in current window</td>
              </tr>
              <tr className="border-b border-neutral-100">
                <td className="py-2 pr-4 font-mono">X-RateLimit-Reset</td>
                <td className="py-2">Seconds until window resets</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="space-y-2">
        <h1
          id="data-validation"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Data Validation
        </h1>
        <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <li>Validate all input data before sending API requests</li>
          <li>Use proper data types and formats </li>
          <li>Implement client-side validation as a first line of defense</li>
        </ul>
      </div>
      <div className="space-y-2">
        <h1
          id="cm-security"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Security Considerations
        </h1>
        <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <li>Never expose API keys in client-side code</li>
          <li>Use HTTPS for all API communications</li>
          <li>Implement proper CORS policies</li>
          <li>Validate and sanitize all API responses</li>
        </ul>
      </div>
    </div>
  );
}
